import { Check } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatCurrencyRange } from '@/lib/formatters'
import { CoverageType } from '@/models/insuranceCoverage'
import { CargoRestrictionDialog } from './CargoRestrictionDialog'
import type { CoverageQuote } from './quoteCardTypes'

const COVERAGE_LABELS: Record<CoverageType, string> = {
  [CoverageType.AUTO_LIABILITY]: 'Auto Liability',
  [CoverageType.AUTO_PHYSICAL_DAMAGE]: 'Physical Damage',
  [CoverageType.GENERAL_LIABILITY]: 'General Liability',
  [CoverageType.MOTOR_TRUCK_CARGO]: 'Cargo',
  [CoverageType.WORKERS_COMPENSATION]: 'Workers Comp',
  [CoverageType.PET]: 'Pet insurance',
}

// Placeholder data for the "View list" dialog — no real truck roster exists in this prototype.
const MOCK_TRUCKS = [
  'Truck #1 · VIN 1FUJA6CV12LU',
  'Truck #4 · VIN 1FUJA6CV45LU',
  'Truck #7 · VIN 1FUJA6CV78LU',
]

/** Dark-red ghost styling for the "Remove from cart" action. */
const REMOVE_BUTTON_CLASS = 'text-red-700 hover:bg-red-50 hover:text-red-800'

/** Liability lines pay from the first dollar, so a deductible they never state is a real zero. */
const PAYS_FROM_FIRST_DOLLAR = new Set<CoverageType>([
  CoverageType.AUTO_LIABILITY,
  CoverageType.GENERAL_LIABILITY,
])

/** A term the carrier never stated. Printing it as $0 reads as a real zero-dollar term. */
function formatTerm(amount: number | null): string {
  if (amount === null) return '—'
  return formatCurrency(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

interface CoverageRowProps {
  coverage: CoverageQuote
  /** Set only for itemized-checkout cards — each coverage gets its own "Add to cart". */
  onAddToCart?: () => void
  /**
   * This coverage is already in the cart. With an add button (itemized checkout) it flips to a
   * disabled "Added" + Remove; without one (summary view) it shows a small "In cart" pill so a
   * partially-added quote reads clearly per coverage.
   */
  isInCart?: boolean
  onRemoveFromCart?: () => void
  /**
   * 'block' (Design 1): full-width primary button below Limit/Deductible.
   * 'inline' (Design 2): small secondary button next to Deductible.
   */
  addToCartVariant?: 'block' | 'inline'
  /** The card is priced as one total (isPricedAsOneTotal): an unpriced line then shows no price, not "Included". */
  pricedAsOneTotal?: boolean
}

/** One coverage box: label + premium and its limit/deductible detail. */
export function CoverageRow({
  coverage,
  onAddToCart,
  isInCart = false,
  onRemoveFromCart,
  addToCartVariant = 'block',
  pricedAsOneTotal = false,
}: CoverageRowProps) {
  const {
    type,
    premium,
    premiumRange,
    limit,
    aggregateLimit,
    deductible,
    comprehensiveDeductible,
    restrictionNote,
    physicalDamageUnitCount,
  } = coverage
  const showsAggregate = type !== CoverageType.AUTO_PHYSICAL_DAMAGE && aggregateLimit != null
  // A carrier that states a retention (GEICO's large fleets) still shows the real number.
  const statedDeductible = PAYS_FROM_FIRST_DOLLAR.has(type) ? (deductible ?? 0) : deductible
  // Unpriced lines are inside the policy total: a $0 would read as cover the carrier is giving away.
  const premiumLabel = premiumRange
    ? formatCurrencyRange(premiumRange)
    : premium !== null
      ? formatCurrency(premium)
      : pricedAsOneTotal
        ? null
        : 'Included'

  return (
    <div className="bg-ink-50/60 flex flex-col gap-3 rounded-xl p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-foreground text-md font-semibold">{COVERAGE_LABELS[type]}</span>
          {restrictionNote && (
            <div className="text-muted-foreground text-xs">
              {restrictionNote} <CargoRestrictionDialog note={restrictionNote} affectedTrucks={MOCK_TRUCKS} />
            </div>
          )}
        </div>
        {premiumLabel && (
          <span className="text-foreground text-md shrink-0 font-semibold">{premiumLabel}</span>
        )}
      </div>
      <div className="flex items-end gap-3">
        <dl className="grid grid-cols-[minmax(11ch,max-content)_auto] items-start gap-3 text-sm">
          <div>
            <dt className="text-muted-foreground mb-1 text-xs">Limit</dt>
            {/* A physical damage limit totals every unit's stated value; $0 or none means we don't know it. */}
            <dd className="text-foreground font-semibold">
              {type !== CoverageType.AUTO_PHYSICAL_DAMAGE
                ? formatTerm(limit)
                : !limit
                  ? 'Stated Value'
                  : formatTerm(limit)}
              {type === CoverageType.AUTO_PHYSICAL_DAMAGE && !!limit && !!physicalDamageUnitCount && (
                <span className="text-muted-foreground text-xs font-normal">
                  {' '}
                  for {physicalDamageUnitCount} <br />
                  {physicalDamageUnitCount === 1 ? 'truck or trailer' : 'trucks and trailers'}
                </span>
              )}
              {showsAggregate && (
                <span className="text-muted-foreground text-xs font-normal"> occurrence</span>
              )}
            </dd>
            {/* Same shape as the two deductibles below: amount first, which limit it is after. */}
            {showsAggregate && (
              <dd className="text-foreground font-semibold">
                {formatTerm(aggregateLimit)}
                <span className="text-muted-foreground text-xs font-normal"> aggregate</span>
              </dd>
            )}
          </div>
          <div>
            <dt className="text-muted-foreground mb-1 text-xs">Deductible</dt>
            {/* Physical damage carries two: printing either alone misstates the other. */}
            <dd className="text-foreground font-semibold">
              {formatTerm(statedDeductible)}
              {comprehensiveDeductible != null && (
                <span className="text-muted-foreground text-xs font-normal"> collision</span>
              )}
            </dd>
            {comprehensiveDeductible != null && (
              <dd className="text-foreground font-semibold">
                {formatTerm(comprehensiveDeductible)}
                <span className="text-muted-foreground text-xs font-normal"> comprehensive</span>
              </dd>
            )}
          </div>
        </dl>
        {/* Summary view (no add button): a neutral per-coverage "In cart" marker. */}
        {!onAddToCart && isInCart && (
          <Badge className="border-ink-200 bg-ink-100 text-ink-900 ml-auto gap-1">
            <Check className="size-3" />
            In cart
          </Badge>
        )}
        {onAddToCart &&
          addToCartVariant === 'inline' &&
          (isInCart ? (
            <div className="ml-auto flex flex-col items-end gap-0.5">
              <Button variant="outline" size="xs" disabled>
                Added
              </Button>
              <Button variant="ghost" size="xs" onClick={onRemoveFromCart} className={REMOVE_BUTTON_CLASS}>
                Remove
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="xs" onClick={onAddToCart} className="ml-auto">
              Add to cart
            </Button>
          ))}
      </div>
      {onAddToCart &&
        addToCartVariant === 'block' &&
        (isInCart ? (
          <div className="flex flex-col gap-1">
            <Button className="w-full" size="sm" disabled>
              Added to cart
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemoveFromCart} className={REMOVE_BUTTON_CLASS}>
              Remove
            </Button>
          </div>
        ) : (
          <Button className="w-full" size="sm" onClick={onAddToCart}>
            Add to cart
          </Button>
        ))}
    </div>
  )
}
